import React, { useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, TextField, Chip, Box, Typography, Autocomplete } from '@mui/material';
import getAllTiposIndicador from 'entity/TipoIndicador/all';
import { getAllIndicadores } from 'entity/Indicador/all';
import Equipamento from 'entity/Equipamento';
import { fetchArray } from 'entity';

export default function SeletorConfiguracaoEquipamento({ configuracao, setConfiguracao }) {
    const [equipamentos, setEquipamentos] = useState([]);
    const [equipamentosLoaded, setEquipamentosLoaded] = useState(false);

    // Carregar equipamentos quando necessário
    const loadEquipamentos = async () => {
        if (!equipamentosLoaded) {
            try {
                const query = await Equipamento.query();
                const result = await fetchArray(query.get());
                console.log("result ", result);

                // Verificar se result é um array
                if (Array.isArray(result)) {
                    setEquipamentos(result);
                } else {
                    console.warn('Result is not an array:', result);
                    setEquipamentos([]);
                }
                setEquipamentosLoaded(true);
            } catch (error) {
                console.error('Erro ao carregar equipamentos:', error);
                setEquipamentos([]);
                setEquipamentosLoaded(true);
            }
        }
    };

    // get tipo indicador
    const tiposIndicador = getAllTiposIndicador().map(TipoClass => ({
        value: TipoClass.field,
        label: TipoClass.fullName,
        class: TipoClass
    }));

    // get all indicadores
    const todosIndicadores = getAllIndicadores().map(IndicadorClass => ({
        value: IndicadorClass.field,
        label: IndicadorClass.fullName,
        abbrev: IndicadorClass.abbrev,
        class: IndicadorClass
    }));

    // map indicadores by tipo indicador
    const getIndicadoresDoTipo = (tipoField) => {
        const tipoSelecionado = tiposIndicador.find(t => t.value === tipoField);
        if (!tipoSelecionado) return todosIndicadores;

        const indicadoresDoTipo = tipoSelecionado.class.indicadores.map(IndicadorClass => ({
            value: IndicadorClass.field,
            label: IndicadorClass.fullName,
            abbrev: IndicadorClass.abbrev,
            class: IndicadorClass
        }));

        return indicadoresDoTipo;
    };

    const indicadoresDisponiveis = getIndicadoresDoTipo(configuracao.tipoIndicador);

    const condicoesAtualizacao = [
        { value: 'todos', label: 'Todos os registros' },
        { value: 'vazio', label: 'Apenas quando equipamento está vazio' },
        { value: 'custom', label: 'Valor personalizado' }
    ];

    const handleChange = (field, value) => {
        // if type has changed, clear the selected indicators
        if (field === 'tipoIndicador') {
            setConfiguracao(prev => ({
                ...prev,
                [field]: value,
                indicadores: [] // clear indicators when changing type
            }));
        } else {
            setConfiguracao(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Tipo de Indicador</InputLabel>
                    <Select
                        value={configuracao.tipoIndicador}
                        onChange={(e) => handleChange('tipoIndicador', e.target.value)}
                        label="Tipo de Indicador"
                    >
                        {tiposIndicador.map((tipo) => (
                            <MenuItem key={tipo.value} value={tipo.value}>
                                {tipo.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    multiple
                    options={indicadoresDisponiveis.map(i => i.value)}
                    getOptionLabel={(option) =>
                        indicadoresDisponiveis.find(i => i.value === option)?.label || option
                    }
                    value={configuracao.indicadores || []}
                    onChange={(_, value) => handleChange('indicadores', value)}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={indicadoresDisponiveis.find(i => i.value === option)?.label || option}
                                {...getTagProps({ index })}
                                key={option}
                            />
                        ))
                    }
                    sx={{ minWidth: 300 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Indicadores"
                            variant="outlined"
                            helperText={`Disponíveis para ${tiposIndicador.find(t => t.value === configuracao.tipoIndicador)?.label || 'este tipo'}: ${indicadoresDisponiveis.length}`}
                        />
                    )}
                />
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel>Condição para Atualização</InputLabel>
                    <Select
                        value={configuracao.condicao}
                        onChange={(e) => handleChange('condicao', e.target.value)}
                        label="Condição para Atualização"
                    >
                        {condicoesAtualizacao.map((condicao) => (
                            <MenuItem key={condicao.value} value={condicao.value}>
                                {condicao.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {configuracao.condicao === 'custom' && (
                    <TextField
                        label="Valor da Condição"
                        value={configuracao.valorCondicao || ''}
                        onChange={(e) => handleChange('valorCondicao', e.target.value)}
                        placeholder="Ex: Nome do equipamento"
                        sx={{ minWidth: 150 }}
                    />
                )}

                <Autocomplete
                    options={equipamentos}
                    getOptionLabel={(option) => option.equipamento || ''}
                    value={configuracao.novoEquipamento || null}
                    onChange={(_, value) => handleChange('novoEquipamento', value)}
                    onOpen={loadEquipamentos}
                    sx={{ minWidth: 200 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Novo Equipamento"
                            variant="outlined"
                            required
                        />
                    )}
                />
            </div>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Resumo:</strong> Atualizar indicadores <strong>{configuracao.indicadores?.join(', ')}</strong>
                {' '}do tipo <strong>{tiposIndicador.find(t => t.value === configuracao.tipoIndicador)?.label}</strong>
                {' '}para <strong>"{configuracao.novoEquipamento?.equipamento || 'Novo equipamento'}"</strong>
                {' '}quando {condicoesAtualizacao.find(c => c.value === configuracao.condicao)?.label.toLowerCase()}
                {configuracao.condicao === 'custom' && configuracao.valorCondicao &&
                    ` "${configuracao.valorCondicao}"`
                }
            </Typography>
        </Box>
    );
}
